export type MeetingDraftInput = {
  draftId: string;
  name: string;
  email: string;
  organization: string;
  intent: "customer" | "investor" | "talent";
  message: string;
  availability: string;
};

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function operationResult(value: unknown): JsonRecord {
  if (!isRecord(value)) {
    return {};
  }

  for (const key of ["data", "result", "payload", "output"]) {
    if (isRecord(value[key])) {
      return value[key] as JsonRecord;
    }
  }

  return value;
}

export async function saveMeetingDraft(
  draft: MeetingDraftInput,
  submitted: boolean,
): Promise<void> {
  // cikada-public-meeting fronts the collaborator-only inbox for signed-out
  // visitors: it forwards save_meeting_draft and refuses every other action.
  // `source` is reserved by /ops/invoke, so the label travels as source_label.
  const response = await fetch(
    "/api/cikada/cikada-public-meeting-api/ops/receive",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        payload: {
          action: "save_meeting_draft",
          draft_id: draft.draftId,
          name: draft.name,
          email: draft.email,
          organization: draft.organization,
          intent: draft.intent,
          message: draft.message,
          availability: draft.availability,
          submitted,
          notice_version: "2026-08-04",
          source_label: "cikada_web",
        },
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Meeting draft request failed (${response.status}).`);
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return;
  }

  const result = operationResult(await response.json());
  if (result.ok === false) {
    throw new Error("The meeting draft could not be saved.");
  }
}